/**
 * @file CreditAllocations.js
 * @version 2.0.3
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-19
 * @modified 2026-09-29
 * @description Review screen for credit notes and overpayments still carrying
 *              money: what each is worth, what it would do to the customer's
 *              balance, and where it could be allocated. Allocation itself runs
 *              through the existing AllocateCreditNoteModal, which pushes to
 *              Xero and updates locally — deliberately not reimplemented here.
 *
 *              Built to the shape every other search screen uses — header,
 *              filter row, table toolbar, sortable and right-click-filterable
 *              columns, expandable rows — rather than the one-off layout it
 *              started as. The endpoint returns the whole set in one go, so
 *              sorting, filtering and pagination all happen here; the column
 *              filter hook falls back to page-scoped mode when it is given no
 *              server facets, which is exactly right for that.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_URL } from '../config';
import './CreditAllocations.css';
import './TableStyles.css';
import './SearchLayout.css';
import LoadingSpinner from './LoadingSpinner';
import Input from './Input';
import Dropdown from './Dropdown';
import DateInput from './DateInput';
import PaginationControls from './PaginationControls';
import ColumnFilterMenu from './ColumnFilterMenu';
import ColumnFilterNote from './ColumnFilterNote';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { matchesAllTerms } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';
import ExportTableButton from './ExportTableButton';
import { showAlert } from './Alert';
import AllocateCreditNoteModal from './AllocateCreditNoteModal';
import InvoiceModal from './InvoiceModal';
import { useSlideOutButton } from './SlideOutButton';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import usePersistedState from '../hooks/usePersistedState';
import useColumnFilters from '../hooks/useColumnFilters';
import EntityLink from './EntityLink';

/**
 * Right-click a header to narrow by the values in the result set.
 *
 * Only columns whose values repeat. A filter on the document number or an
 * amount would be a list where every option matches one row — a worse way to
 * find that row than reading it off the table.
 */
const COLUMN_FILTERS = {
  doctype: { label: 'Type', value: (c) => (c.transaction_type === 'OVERPAYMENT' ? 'Overpayment' : 'Credit note') },
  customer: { label: 'Customer', value: (c) => c.company_name },
  status: { label: 'Status', value: (c) => c.status },
  state: { label: 'Allocatable', value: (c) => blockedReason(c) || 'Ready to allocate' }
};

/** Why this credit cannot be allocated, or null when it can. */
function blockedReason(c) {
  if (c.blocked_not_in_xero) return 'Not in Xero yet';
  if (c.blocked_nothing_to_allocate) return 'Nothing to allocate against';
  return null;
}

const AGE_BANDS = [
  { value: 'all', label: 'Any age' },
  { value: '30', label: '30 days and over' },
  { value: '60', label: '60 days and over' },
  { value: '90', label: '90 days and over' },
  { value: '180', label: '180 days and over' }
];

const EMPTY_FILTERS = {
  search: '',
  ageBand: 'all',
  dateFrom: '',
  dateTo: '',
  minRemaining: '',
  showBlocked: false,
  limit: '25'
};

function CreditAllocations({ user }) {
  const [credits, setCredits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState({});
  const [loadingSuggestion, setLoadingSuggestion] = useState(null);
  const [allocating, setAllocating] = useState(null);
  const [viewingInvoiceId, setViewingInvoiceId] = useState(null);

  const [filters, setFilters] = usePersistedState(
    'creditAllocations.filters', EMPTY_FILTERS, { merge: true });
  const [expanded, setExpanded] = usePersistedState('creditAllocations.expanded', []);
  const [page, setPage] = usePersistedState('creditAllocations.page', 1);
  const [sortField, setSortField] = usePersistedState('creditAllocations.sortField', 'remaining');
  const [sortDirection, setSortDirection] = usePersistedState('creditAllocations.sortDirection', 'desc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/invoices/credit-notes/unallocated`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to load');
      setCredits(data.credits || []);
      setSummary(data.summary || null);
    } catch (err) {
      showAlert(`Could not load unallocated credits: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /*
   * The search-header filters, applied before the column filters so the
   * right-click menus offer what survived them — otherwise the menu lists
   * values that cannot appear in the table.
   */
  const filtered = useMemo(() => {
    const minRemaining = parseFloat(filters.minRemaining);
    const minAge = filters.ageBand === 'all' ? null : Number(filters.ageBand);
    return credits.filter((c) => {
      // Blocked rows are the ones nobody can act on, so they are out of the way
      // by default — but shown rather than deleted, because "why is this not in
      // the list" is a question the screen should be able to answer.
      if (!filters.showBlocked && blockedReason(c)) return false;
      if (!matchesAllTerms([c.company_name, c.invoice_no, c.account_no], filters.search, {
        amounts: [c.credit_total, c.remaining, c.customer_outstanding, c.outstanding_after],
      })) return false;
      if (minAge !== null && Number(c.age_days) < minAge) return false;
      if (Number.isFinite(minRemaining) && Number(c.remaining) < minRemaining) return false;
      if (filters.dateFrom && c.invoice_date < filters.dateFrom) return false;
      if (filters.dateTo && c.invoice_date > filters.dateTo) return false;
      return true;
    });
  }, [credits, filters]);

  const cols = useColumnFilters(filtered, COLUMN_FILTERS, {
    storageKey: 'creditAllocations.columnFilters',
    // Back to page 1: staying on page 4 of a set that just shrank to one shows
    // an empty table for no visible reason.
    onChange: () => setPage(1)
  });

  const sorted = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    const read = (c) => {
      switch (sortField) {
        case 'invoice_no': return (c.invoice_no || '').toLowerCase();
        case 'customer': return (c.company_name || '').toLowerCase();
        case 'credit_total': return Number(c.credit_total) || 0;
        case 'age_days': return Number(c.age_days) || 0;
        case 'customer_outstanding': return Number(c.customer_outstanding) || 0;
        case 'outstanding_after': return Number(c.outstanding_after) || 0;
        default: return Number(c.remaining) || 0;
      }
    };
    // Copied first: sort() mutates, and this array is the hook's output.
    return [...cols.rows].sort((a, b) => {
      const x = read(a); const y = read(b);
      if (x < y) return -1 * dir;
      if (x > y) return 1 * dir;
      return 0;
    });
  }, [cols.rows, sortField, sortDirection]);

  const limit = Number(filters.limit) || 25;
  const totalPages = Math.max(1, Math.ceil(sorted.length / limit));
  // A filter can shrink the set under the current page while it is being typed.
  const safePage = Math.min(page, totalPages);
  const visible = useMemo(
    () => sorted.slice((safePage - 1) * limit, safePage * limit),
    [sorted, safePage, limit]);

  const pagination = { page: safePage, limit, total: sorted.length, totalPages };

  useSlideOutButton([loading, sorted.length, expanded.length]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    cols.clearEverything();
    setPage(1);
  };

  // Any filter at all — header or column — lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, EMPTY_FILTERS, {
    labels: {
      ageBand: 'Age', minRemaining: 'Minimum unallocated',
      showBlocked: 'Blocked', date: 'Document date'
    }
  });
  const filtersApplied = headerFilters.count > 0 || cols.activeCount > 0;

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'invoice_no' || field === 'customer' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const sortArrow = (field) =>
    (sortField === field ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : '');

  /** Fetch a credit's suggestions if they are not already held, then open it. */
  const fetchSuggestions = useCallback(async (id) => {
    if (suggestions[id]) return suggestions[id];
    setLoadingSuggestion(id);
    try {
      const res = await fetch(`${API_URL}/api/invoices/credit-notes/${id}/suggestions`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to load suggestions');
      setSuggestions((prev) => ({ ...prev, [id]: data }));
      return data;
    } catch (err) {
      showAlert(`Could not work out where this credit could go: ${err.message}`, 'error');
      return null;
    } finally {
      setLoadingSuggestion(null);
    }
  }, [suggestions]);

  const toggleRow = async (credit) => {
    if (expanded.includes(credit.id)) {
      setExpanded(expanded.filter((id) => id !== credit.id));
      return;
    }
    const data = await fetchSuggestions(credit.id);
    if (data) setExpanded([...expanded, credit.id]);
  };

  const allExpanded = visible.length > 0 && visible.every((c) => expanded.includes(c.id));

  /**
   * Expand every row on this page, fetching what is missing.
   *
   * Scoped to the page rather than the whole set: each row is its own request,
   * and opening 400 of them to look at 25 is a great deal of work nobody asked
   * for.
   */
  const handleExpandAll = async () => {
    if (allExpanded) { setExpanded([]); return; }
    const missing = visible.filter((c) => !suggestions[c.id] && !blockedReason(c));
    if (missing.length) {
      setLoadingSuggestion('all');
      const results = await Promise.all(missing.map((c) =>
        fetch(`${API_URL}/api/invoices/credit-notes/${c.id}/suggestions`, { credentials: 'include' })
          .then((r) => r.json())
          .then((d) => (d.success ? [c.id, d] : null))
          .catch(() => null)
      ));
      setSuggestions((prev) => ({
        ...prev, ...Object.fromEntries(results.filter(Boolean))
      }));
      setLoadingSuggestion(null);
    }
    setExpanded(visible.map((c) => c.id));
  };

  /** What produced this list, printed above it so the page can be checked. */
  const exportCriteria = () => {
    const out = [];
    if (filters.search) out.push({ label: 'Search', value: filters.search });
    if (filters.ageBand !== 'all') {
      out.push({ label: 'Age', value: AGE_BANDS.find((b) => b.value === filters.ageBand)?.label });
    }
    if (filters.minRemaining) out.push({ label: 'Minimum unallocated', value: formatCurrency(filters.minRemaining) });
    if (filters.dateFrom || filters.dateTo) {
      out.push({ label: 'Document date', value: `${filters.dateFrom || 'any'} to ${filters.dateTo || 'any'}` });
    }
    out.push({
      label: 'Blocked credits',
      value: filters.showBlocked ? 'Shown' : 'Hidden'
    });
    // Column filters are the reason two exports of "the same" search can
    // differ, so they are named rather than folded into the row count.
    for (const [key, set] of Object.entries(cols.selected)) {
      if (!Array.isArray(set)) continue;
      out.push({ label: cols.definitions[key]?.label || key, value: set.join(', ') || 'none' });
    }
    return out;
  };

  const buildExport = () => ({
    title: 'Unallocated Credits',
    kicker: 'UNALLOCATED CREDITS',
    rowNoun: sorted.length === 1 ? 'credit' : 'credits',
    filename: `unallocated-credits-${new Date().toISOString().slice(0, 10)}`,
    landscape: true,
    columns: [
      { key: 'doc', label: 'Document', width: '12%' },
      { key: 'type', label: 'Type', width: '10%' },
      { key: 'customer', label: 'Customer', width: '24%' },
      { key: 'date', label: 'Date', width: '9%' },
      { key: 'credit', label: 'Credit', width: '9%', numeric: true },
      { key: 'remaining', label: 'Unallocated', width: '10%', numeric: true },
      { key: 'age', label: 'Age', width: '6%', numeric: true },
      { key: 'owed', label: 'They owe', width: '10%', numeric: true },
      { key: 'after', label: 'After', width: '10%', numeric: true }
    ],
    // Everything the filters left, not just the page on screen — a report of
    // "what is outstanding" that stops at row 25 is not the report.
    rows: sorted.map((c) => ({
      doc: c.invoice_no || '',
      type: c.transaction_type === 'OVERPAYMENT' ? 'Overpayment' : 'Credit note',
      customer: c.company_name || '',
      date: formatDateShort(c.invoice_date),
      credit: formatCurrency(c.credit_total),
      remaining: formatCurrency(c.remaining),
      age: `${c.age_days}d`,
      owed: formatCurrency(c.customer_outstanding),
      after: formatCurrency(c.outstanding_after),
      detail: {
        columns: [
          { key: 'invoice', label: 'Invoice', width: '20%' },
          { key: 'due', label: 'Due', width: '18%' },
          { key: 'past', label: 'Days past due', width: '18%', numeric: true },
          { key: 'outstanding', label: 'Outstanding', width: '22%', numeric: true },
          { key: 'apply', label: 'Apply', width: '22%', numeric: true }
        ],
        emptyLabel: blockedReason(c) || 'Nothing outstanding — this credit stays on account',
        rows: (suggestions[c.id]?.suggestions || []).map((s) => ({
          invoice: s.invoiceNo,
          due: formatDateShort(s.dueDate),
          past: String(s.daysPastDue),
          outstanding: formatCurrency(s.outstanding),
          apply: formatCurrency(s.amount)
        }))
      }
    })),
    totals: [{
      doc: `${sorted.length} credits`,
      remaining: formatCurrency(sorted.reduce((sum, c) => sum + (Number(c.remaining) || 0), 0))
    }],
    scopeNote: 'Every credit matching the filters, not only the page that was on screen. '
      + 'Expanded detail is included for credits whose suggestions had been loaded.',
    criteria: exportCriteria()
  });

  return (
    <div className="credit-allocations">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Unallocated Credits</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              Credit notes and overpayments still holding money. Until they are allocated
              they make balances look worse than they are, and the customer stays owed.
            </span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <ExportTableButton
                build={buildExport}
                user={user}
                rowCount={sorted.length}
                title="Unallocated Credits"
                modalTitle="Export unallocated credits"
                onError={(message) => showAlert(message, 'error')}
              />
            </div>
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={load} disabled={loading} title="Reload from the database">
                <div className="icon-wrapper">
                  <span className={`material-symbols-rounded${loading ? ' spinning' : ''}`}>refresh</span>
                </div>
                <span className="btn-label">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {summary && (
        <div className="ca-summary">
          <div className="ca-stat">
            <span className="k">Outstanding</span>
            <span className="v">{formatCurrency(summary.totalRemaining)}</span>
            <span className="s">{summary.count} documents</span>
          </div>
          <div className="ca-stat">
            <span className="k">Ready to allocate</span>
            <span className="v">{summary.allocatable}</span>
            <span className="s">have open invoices</span>
          </div>
          <div className="ca-stat">
            <span className="k">Nothing to apply to</span>
            <span className="v">{summary.blockedNothingToAllocate}</span>
            <span className="s">stay on account</span>
          </div>
          <div className="ca-stat">
            <span className="k">Not in Xero</span>
            <span className="v">{summary.blockedNotInXero}</span>
            <span className="s">push before allocating</span>
          </div>
        </div>
      )}

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp amounts fields={['document no.', 'customer', 'account no.', 'credit', 'unallocated', 'they owe', 'after allocating']} />
            </label>
            <Input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Customer, account or document number..."
              icon="search"
            />
          </div>

          <div className="filter-group">
            <label>Age</label>
            <Dropdown
              value={filters.ageBand}
              onChange={(value) => handleFilterChange('ageBand', value)}
              options={AGE_BANDS}
            />
          </div>

          <div className="filter-group">
            <DateInput
              label="From Date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <DateInput
              label="To Date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Min unallocated</label>
            <Input
              type="number"
              value={filters.minRemaining}
              onChange={(e) => handleFilterChange('minRemaining', e.target.value)}
              placeholder="Any"
              icon="payments"
            />
          </div>

          {/* Off by default: these are the rows nobody can act on, and they
              crowd out the ones somebody can. */}
          <div className="filter-group">
            <label>Show blocked</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="ca-show-blocked"
                  type="checkbox"
                  checked={filters.showBlocked}
                  onChange={(e) => handleFilterChange('showBlocked', e.target.checked)}
                />
                <label htmlFor="ca-show-blocked"></label>
              </div>
            </div>
          </div>

          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(value) => handleFilterChange('limit', value)}
              options={[
                { value: '25', label: '25' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]}
            />
          </div>
        </div>
      </div>

      <div className="credit-allocations-panel">
        <div className="table-toolbar">
          <button
            className="btn-secondary btn-sm"
            onClick={handleExpandAll}
            disabled={loading || visible.length === 0 || loadingSuggestion === 'all'}
          >
            <span className="material-symbols-rounded">
              {allExpanded ? 'unfold_less' : 'unfold_more'}
            </span>
            {loadingSuggestion === 'all' ? 'Loading...' : (allExpanded ? 'Collapse All' : 'Expand All')}
          </button>

          {/* A column filter is invisible from anywhere but the header it was
              set on, and a table quietly missing rows is how people stop
              trusting a screen. */}
          <ColumnFilterNote cols={cols} header={headerFilters} onClear={handleClearFilters} />

          {sorted.length > 0 && (
            <PaginationControls pagination={pagination} onPageChange={setPage} />
          )}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : sorted.length === 0 ? (
          <div className="ca-empty">
            {credits.length === 0
              ? 'Nothing unallocated — every credit note and overpayment has been applied.'
              : 'No credits match these filters.'}
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table credit-allocations-table">
              <thead>
                <tr>
                  <th className="expand-col"></th>
                  <th className="sortable" onClick={() => handleSort('invoice_no')}>
                    Document{sortArrow('invoice_no')}
                  </th>
                  <th {...cols.headerProps('doctype')}>Type</th>
                  <th
                    {...cols.headerProps('customer')}
                    className={`sortable${cols.isFiltered('customer') ? ' col-filtered' : ''}`}
                    onClick={() => handleSort('customer')}
                  >
                    Customer{sortArrow('customer')}
                  </th>
                  <th className="text-right sortable" onClick={() => handleSort('credit_total')}>
                    Credit{sortArrow('credit_total')}
                  </th>
                  <th className="text-right sortable" onClick={() => handleSort('remaining')}>
                    Unallocated{sortArrow('remaining')}
                  </th>
                  <th className="text-right sortable" onClick={() => handleSort('age_days')}>
                    Age{sortArrow('age_days')}
                  </th>
                  <th className="text-right sortable" onClick={() => handleSort('customer_outstanding')}>
                    They owe{sortArrow('customer_outstanding')}
                  </th>
                  <th className="text-right sortable" onClick={() => handleSort('outstanding_after')}>
                    After allocating{sortArrow('outstanding_after')}
                  </th>
                  <th {...cols.headerProps('state')}>Status</th>
                  <th className="actions-col"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const blocked = blockedReason(c);
                  const sug = suggestions[c.id];
                  const isOpen = expanded.includes(c.id);
                  return (
                    <React.Fragment key={c.id}>
                      <tr className={`expandable-row${isOpen ? ' expanded' : ''}${blocked ? ' ca-blocked' : ''}`}>
                        <td className="expand-col">
                          <button
                            className="btn-icon btn-expand"
                            onClick={() => toggleRow(c)}
                            disabled={!!blocked || loadingSuggestion === c.id}
                            title={blocked || (isOpen ? 'Hide suggestions' : 'Show where this credit could be applied')}
                          >
                            <span className={`material-symbols-rounded${loadingSuggestion === c.id ? ' spinning' : ''}`}>
                              {loadingSuggestion === c.id ? 'progress_activity' : (isOpen ? 'expand_less' : 'expand_more')}
                            </span>
                          </button>
                        </td>
                        <td>
                          <span className="ca-docno">
                            <EntityLink type="credit_note" id={c.id}>{c.invoice_no}</EntityLink>
                          </span>
                        </td>
                        <td>
                          <span className="ca-doctype">
                            {c.transaction_type === 'OVERPAYMENT' ? 'Overpayment' : 'Credit note'}
                          </span>
                        </td>
                        <td>
                          <EntityLink type="contact" id={c.account_id}>{c.company_name}</EntityLink>
                          {c.account_no && (
                            <span className="account-badge-inline">
                              <EntityLink type="contact" id={c.account_id} variant="badge">{c.account_no}</EntityLink>
                            </span>
                          )}
                        </td>
                        <td className="text-right">{formatCurrency(c.credit_total)}</td>
                        <td className="text-right ca-remaining">{formatCurrency(c.remaining)}</td>
                        <td className="text-right">{c.age_days}d</td>
                        <td className="text-right">{formatCurrency(c.customer_outstanding)}</td>
                        <td className="text-right ca-after">{formatCurrency(c.outstanding_after)}</td>
                        <td>
                          {blocked
                            ? <span className="ca-pill ca-pill-blocked">{blocked}</span>
                            : <span className="ca-pill ca-pill-ready">Ready</span>}
                        </td>
                        <td>
                          {/* Every row renders the same three buttons — disabled
                              with a reason rather than omitted, so the column
                              does not go ragged between row types. */}
                          <div className="actions-cell">
                            <button
                              className={`btn-icon${blocked ? ' btn-disabled' : ''}`}
                              title={blocked || 'Show where this credit could be applied'}
                              disabled={!!blocked || loadingSuggestion === c.id}
                              onClick={() => toggleRow(c)}
                            >
                              <span className="material-symbols-rounded">lightbulb</span>
                            </button>
                            <button
                              data-write-action
                              className={`btn-icon${blocked ? ' btn-disabled' : ''}`}
                              title={blocked || 'Allocate this credit'}
                              disabled={!!blocked}
                              onClick={() => setAllocating(c)}
                            >
                              <span className="material-symbols-rounded">call_split</span>
                            </button>
                            <button
                              className="btn-icon"
                              title="View this document"
                              onClick={() => setViewingInvoiceId(c.id)}
                            >
                              <span className="material-symbols-rounded">visibility</span>
                            </button>
                          </div>
                        </td>
                      </tr>

                      <tr className={`expanded-content${isOpen ? ' expanded' : ''}`}>
                        <td colSpan={11}>
                          {isOpen && sug && (
                            <div className="ca-suggestions">
                              {sug.suggestions.length === 0 ? (
                                <p className="ca-none">
                                  Nothing outstanding for this customer — the credit stays on account.
                                </p>
                              ) : (
                                <>
                                  <p className="ca-sug-intro">
                                    Oldest debt first — this would clear{' '}
                                    <strong>{formatCurrency(sug.remaining - sug.unapplied)}</strong>
                                    {sug.unapplied > 0.005 && (
                                      <> and leave <strong>{formatCurrency(sug.unapplied)}</strong> on account</>
                                    )}.
                                  </p>
                                  <table className="line-items-table">
                                    <thead>
                                      <tr>
                                        <th>Invoice</th>
                                        <th>Due</th>
                                        <th className="text-right">Days past due</th>
                                        <th className="text-right">Outstanding</th>
                                        <th className="text-right">Apply</th>
                                        <th></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {sug.suggestions.map((s) => (
                                        <tr key={s.invoiceId}>
                                          <td>
                                            <button
                                              type="button"
                                              className="ca-link"
                                              onClick={() => setViewingInvoiceId(s.invoiceId)}
                                            >
                                              {s.invoiceNo}
                                            </button>
                                          </td>
                                          <td>{formatDateShort(s.dueDate)}</td>
                                          <td className="text-right">{s.daysPastDue}</td>
                                          <td className="text-right">{formatCurrency(s.outstanding)}</td>
                                          <td className="text-right ca-apply">{formatCurrency(s.amount)}</td>
                                          <td>
                                            {s.settlesInFull
                                              ? <span className="ca-pill ca-pill-full">Settles in full</span>
                                              : <span className="ca-pill">Part payment</span>}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  <p className="ca-sug-note">
                                    A suggestion only — nothing is applied until you allocate and confirm.
                                  </p>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ColumnFilterMenu cols={cols} />

      {allocating && (
        <AllocateCreditNoteModal
          isOpen={!!allocating}
          creditNote={allocating}
          onClose={() => setAllocating(null)}
          onSuccess={() => {
            setAllocating(null);
            setSuggestions({});
            load();
          }}
        />
      )}

      {viewingInvoiceId && (
        <InvoiceModal
          invoiceId={viewingInvoiceId}
          onClose={() => { setViewingInvoiceId(null); load(); }}
        />
      )}
    </div>
  );
}

export default CreditAllocations;
